import { useCallback, useRef, useState } from 'react';

import {
  Camera01Icon,
  CameraOff01Icon,
  Idea01Icon,
  InformationCircleIcon,
  Settings01Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import Webcam from 'react-webcam';
import { CustomButton } from 'views/CandidateApp/components/CustomButton/CustomButton';

import { Accordion } from 'components/Accordion/Accordion';
import { FormMessage, MessageType, ValidationAlert } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';

import styles from './Selfie.module.scss';

const Screens = {
  Intro: 'intro',
  Camera: 'camera',
  Confirm: 'confirm',
};

const CAMERA_PERMISSION_PLATFORMS = [
  'safariIos',
  'chromeIos',
  'chromeAndroid',
  'safariMac',
  'chromeDesktop',
];

const CAMERA_PERMISSION_PLATFORM_ICONS = {
  safariIos: 'assets/images/icons/safari.png',
  chromeIos: 'assets/images/icons/chrome.png',
  chromeAndroid: 'assets/images/icons/chrome.png',
  safariMac: 'assets/images/icons/safari.png',
  chromeDesktop: 'assets/images/icons/chrome.png',
};

const detectDefaultCameraPermissionPlatform = () => {
  const userAgent = window.navigator?.userAgent || '';
  const isIpadOs = /Macintosh/.test(userAgent) && (window.navigator?.maxTouchPoints || 0) > 1;
  const isIos = /iPad|iPhone|iPod/.test(userAgent) || isIpadOs;
  const isAndroid = /Android/.test(userAgent);
  const isMac = /Macintosh/.test(userAgent) && !isIos;
  const isChrome = /CriOS|Chrome/.test(userAgent) && !/Edg|OPR/.test(userAgent);

  if (isIos) return isChrome ? 'chromeIos' : 'safariIos';
  if (isAndroid) return 'chromeAndroid';
  if (isMac) return isChrome ? 'chromeDesktop' : 'safariMac';
  return 'chromeDesktop';
};

const orderCameraPermissionPlatforms = detectedPlatform => [
  detectedPlatform,
  ...CAMERA_PERMISSION_PLATFORMS.filter(platformKey => platformKey !== detectedPlatform),
];

const Selfie = ({ brand, onSave, screenName = 'intro', noBackButton = false }) => {
  const { t } = useTranslation();
  const webcamRef = useRef(null);
  const [screen, setScreen] = useState(screenName || Screens.Intro);
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [cameraPermissionPlatforms] = useState(() =>
    orderCameraPermissionPlatforms(detectDefaultCameraPermissionPlatform()),
  );
  const [expandedPlatform, setExpandedPlatform] = useState(cameraPermissionPlatforms[0]);

  /** Capture photo */
  const handleCapture = useCallback(() => {
    try {
      const shot = webcamRef.current?.getScreenshot();
      if (!shot) {
        setError('Unable to capture image. Please try again.');
        return;
      }
      setPhoto(shot);
      setError(null);
      setScreen(Screens.Confirm);
    } catch (err) {
      console.error('[Selfie] Capture error:', err);
      setError('An error occurred while capturing the image.');
    }
  }, []);

  /** Save photo */
  const handleSave = useCallback(async () => {
    if (!photo || saving) return;

    setSaving(true);
    setError(null);
    try {
      await onSave(photo);
    } catch (err) {
      console.error('[Selfie] Save error:', err);
      setError('Failed to save selfie. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [photo, saving, onSave]);

  /** Retake */
  const handleRetake = useCallback(() => {
    setPhoto(null);
    setError(null);
    setScreen(Screens.Camera);
  }, []);

  const handleUserMediaError = useCallback(err => {
    if (err?.name === 'NotAllowedError') {
      setPermissionDenied(true);
      return;
    }
    setError('An error occurred while accessing the camera. Please try again.');
  }, []);

  const handleRetryCameraPermission = useCallback(() => {
    setPermissionDenied(false);
    setError(null);
  }, []);

  const handleGoToIntro = useCallback(() => {
    setPermissionDenied(false);
    setError(null);
    setScreen(Screens.Intro);
  }, []);

  const renderCameraPermissionInstructions = () => (
    <div className="d-flex flex-column u-gap-large">
      <div className="flex aspect-[11/3] w-full flex-col items-center justify-center gap-3 rounded-3xl border border-neutral-4 bg-neutral-2 px-6">
        <IconComponent icon={CameraOff01Icon} type="error" border={false} />
        <div className="flex flex-col items-center gap-1 text-center">
          <span className="text-xl font-medium text-neutral-7">
            {t('candidate.selfie.cameraError.notDetectedTitle')}
          </span>
          <span className="text-lg text-neutral-6">
            {t('candidate.selfie.cameraError.notDetectedDescription')}
          </span>
        </div>
      </div>

      <FormMessage
        message={t('candidate.selfie.cameraError.banner')}
        type={MessageType.Warning}
        closeable={false}
        noMargin
      />

      <div className="flex items-center gap-2">
        <HugeiconsIcon
          icon={Settings01Icon}
          size={16}
          strokeWidth={1.5}
          className="text-neutral-7"
        />
        <span className="text-lg font-medium text-neutral-7">
          {t('candidate.selfie.cameraError.howToEnableTitle')}
        </span>
      </div>

      <div className="flex flex-col gap-3">
        {cameraPermissionPlatforms.map(platformKey => (
          <Accordion
            key={platformKey}
            dataTestId={`camera-permission-platform-${platformKey}`}
            className={cn(
              'w-full rounded-xl border px-4 py-3',
              expandedPlatform !== platformKey ? 'bg-neutral-2/10' : 'bg-primary-1/5',
            )}
            collapsed={expandedPlatform !== platformKey}
            onToggle={nextCollapsed => setExpandedPlatform(nextCollapsed ? null : platformKey)}
            header={
              <span className="flex items-center gap-2 font-medium text-neutral-7">
                <img
                  src={CAMERA_PERMISSION_PLATFORM_ICONS[platformKey]}
                  alt=""
                  className="h-6 w-6"
                />
                {(platformKey === 'safariIos' ||
                  platformKey === 'chromeIos' ||
                  platformKey === 'safariMac') && (
                  <img src="assets/images/icons/apple.png" alt="" className="h-6 w-6" />
                )}
                {platformKey === 'chromeAndroid' && (
                  <img src="assets/images/icons/android.png" alt="" className="h-6 w-6" />
                )}
                {t(`candidate.selfie.cameraError.platforms.${platformKey}.title`)}
              </span>
            }>
            <div className="flex flex-col gap-3">
              {t(`candidate.selfie.cameraError.platforms.${platformKey}.description`, {
                defaultValue: '',
              }) && (
                <span className="text-lg text-neutral-6">
                  {t(`candidate.selfie.cameraError.platforms.${platformKey}.description`)}
                </span>
              )}
              {t(`candidate.selfie.cameraError.platforms.${platformKey}.steps`, {
                returnObjects: true,
              }).map((step, index) =>
                typeof step === 'string' ? (
                  <div key={index} className="flex items-start gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-neutral-6 text-xs font-semibold text-white">
                      {index + 1}
                    </span>
                    <span className="text-lg text-neutral-6">{step}</span>
                  </div>
                ) : (
                  <div key={index} className="flex flex-col gap-3">
                    <div className="flex items-start gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-neutral-6 text-xs font-semibold text-white">
                        {index + 1}
                      </span>
                      <span className="text-lg font-medium text-neutral-7">{step.title}</span>
                    </div>
                    <div className="flex flex-col gap-2 pl-9">
                      {step.steps.map((subStep, subIndex) => (
                        <div key={subIndex} className="flex items-start gap-2">
                          <span className="text-lg text-neutral-6">•</span>
                          <span className="text-lg text-neutral-6">{subStep}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ),
              )}
            </div>
          </Accordion>
        ))}
      </div>

      <span className="flex items-start gap-2 sm:items-center">
        <HugeiconsIcon icon={Idea01Icon} size={20} className="shrink-0 text-yellow-2" />
        <span className="text-lg text-neutral-6">
          <span className="font-semibold">{t('candidate.selfie.cameraError.tipLabel')}</span>{' '}
          {t('candidate.selfie.cameraError.tip')}
        </span>
      </span>

      <div className="d-flex align-items-center justify-content-between u-gap-large">
        {!noBackButton && (
          <CustomButton type="secondary" onClick={handleGoToIntro}>
            Back
          </CustomButton>
        )}
        <CustomButton
          brand={brand}
          icon="camera"
          dataTestId="take-selfie-button"
          onClick={handleRetryCameraPermission}>
          {t('candidate.selfie.cameraError.retryButton')}
        </CustomButton>
      </div>

      <span className="text-center text-xl text-text-tertiary">
        {t('candidate.selfie.cameraError.needHelp')}{' '}
        <a href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`} className="underline">
          {t('candidate.selfie.cameraError.contactSupport')} →
        </a>
      </span>
    </div>
  );

  /** Render screens */
  const renderScreen = () => {
    switch (screen) {
      case Screens.Intro:
        return (
          <div className="flex flex-col gap-8">
            <img
              src="assets/images/illustration-selfie.svg"
              className="h-48 w-48"
              alt="Selfie illustration"
            />
            <div className="flex flex-col gap-2">
              <span className="text-4xl font-medium text-neutral-7">Identity Liveness Check</span>
              <p className="text-xl text-neutral-6">
                Take a quick selfie to verify your identity. Your photo will be compared with your
                uploaded ID.
              </p>
            </div>
            <FormMessage
              type={MessageType.Info}
              closeable={false}
              noIcon
              noMargin
              message={
                <div className="flex w-full flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <HugeiconsIcon
                      icon={InformationCircleIcon}
                      size={20}
                      className="shrink-0 text-blue-2"
                    />
                    <span className="text-xl font-semibold text-neutral-7">
                      {t('candidate.selfie.beforeYouBegin.title')}
                    </span>
                  </div>
                  <div className="flex flex-col gap-2 pl-12">
                    <ul className="list-disc space-y-1 text-xl text-neutral-6">
                      {t('candidate.selfie.beforeYouBegin.tips', { returnObjects: true }).map(
                        tip => (
                          <li key={tip}>{tip}</li>
                        ),
                      )}
                    </ul>
                  </div>
                  <div className="flex w-full items-center gap-3 rounded-xl bg-white px-3 py-2.5">
                    <HugeiconsIcon
                      icon={Camera01Icon}
                      size={20}
                      className="shrink-0 text-neutral-7"
                    />
                    <span className="text-xl text-neutral-6">
                      <Trans
                        i18nKey="candidate.selfie.beforeYouBegin.allowPrompt"
                        components={{ bold: <span className="font-semibold text-neutral-7" /> }}
                      />
                    </span>
                  </div>
                </div>
              }
            />
            <CustomButton
              brand={brand}
              small
              dataTestId="take-selfie-button"
              onClick={() => setScreen(Screens.Camera)}>
              Take Selfie
            </CustomButton>
            <span className="text-center text-xl text-text-tertiary">
              {t('candidate.selfie.cameraError.needHelp')}{' '}
              <a href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`} className="underline">
                {t('candidate.selfie.cameraError.contactSupport')} →
              </a>
            </span>
          </div>
        );

      case Screens.Camera:
        if (permissionDenied) {
          return renderCameraPermissionInstructions();
        }

        return (
          <div className="d-flex flex-column u-gap-large">
            {error ? (
              <div className="d-flex align-items-center justify-content-center">
                <img
                  src="assets/images/camera-issue.jpg"
                  alt="Application submitted illustration"
                  className="u-width-75 u-border-radius--half"
                />
              </div>
            ) : (
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                className={cn('u-border u-border-radius--xxx-large')}
                videoConstraints={{ facingMode: 'user' }}
                onUserMediaError={err => handleUserMediaError(err)}
                style={{ objectFit: 'cover' }}
                height={500}
                mirrored
                playsInline
              />
            )}

            {error && <ValidationAlert show={true} message={error} />}

            {!error && (
              <span className="secondary-text u-font-size-small u-margin-0 u-text-centered">
                Hold your face up to the camera viewport
              </span>
            )}

            <div className="d-flex align-items-center justify-content-between u-gap-large">
              {!noBackButton && (
                <CustomButton type="secondary" onClick={() => setScreen(Screens.Intro)}>
                  Back
                </CustomButton>
              )}
              <CustomButton brand={brand} dataTestId="take-selfie-button" onClick={handleCapture}>
                Take Selfie
              </CustomButton>
            </div>
          </div>
        );

      case Screens.Confirm:
        return (
          <div className="d-flex flex-column u-gap-large">
            <img
              src={photo}
              className="u-border u-border-radius--xxx-large"
              alt="Captured selfie"
            />
            {error && <p className="u-text-danger u-margin-top">{error}</p>}
            <div className="d-flex align-items-center justify-content-between u-gap-large">
              <CustomButton
                type="secondary"
                className={cn('u-margin-right', 'u-margin-top--0')}
                onClick={handleRetake}>
                Retake
              </CustomButton>
              <CustomButton
                brand={brand}
                className="u-margin-top--0"
                dataTestId="confirm-selfie-button"
                onClick={handleSave}
                disabled={saving}>
                {saving ? 'Saving…' : 'Confirm Selfie'}
              </CustomButton>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return <div className={styles.selfieWrapper}>{renderScreen()}</div>;
};

export default Selfie;
